import { createContext, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useAuth, useTranslation } from 'src/hooks';
import { Typography } from 'src/components/atoms';
import { Dialog } from 'src/components/organisms';

interface PromptContextProviderProps {
  children: ReactNode;
}

export const PromptContext = createContext<() => Promise<void>>(() => Promise.resolve());

export const PromptProvider = ({ children }: PromptContextProviderProps) => {
  const { t } = useTranslation();
  const cbReject = useRef<() => void>();
  const cbResolve = useRef<() => void>();
  const [open, setOpen] = useState(false);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      cbReject.current = undefined;
      cbResolve.current = undefined;
      setOpen(false);
    }
  }, [isAuthenticated]);

  const showPrompt = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        cbReject.current = reject;
        cbResolve.current = resolve;

        setOpen(true);
      }),
    []
  );

  const handleClose = useCallback(() => {
    if (cbReject.current) {
      cbReject.current();
    }

    setOpen(false);
  }, []);

  const handleConfirm = useCallback(() => {
    if (cbResolve.current) {
      cbResolve.current();
    }
    setOpen(false);
  }, []);

  return (
    <PromptContext.Provider value={showPrompt}>
      {children}
      {isAuthenticated && (
        <Dialog
          open={open}
          onClose={handleClose}
          title={t('defaults.prompt.title')}
          defaultActionButtons={{
            btnConfirmText: t('defaults.prompt.btn_stay'),
            action: handleClose,
            btnCancelText: t('defaults.prompt.btn_leave'),
            cancelAction: handleConfirm
          }}
        >
          <Typography align='center'>{t('defaults.prompt.text')}</Typography>
        </Dialog>
      )}
    </PromptContext.Provider>
  );
};
